import React, { useState, useEffect, Children } from 'react'
import PropTypes from 'prop-types'
import capitalize from 'lodash/capitalize'
import trim from 'lodash/trim'
import Grid from '@mui/material/Grid'

import { toast } from 'react-toastify'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { DialogContent, Box } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '@mui/material/Tooltip'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { getCustomerDetails } from 'middleware/actions/customers'
import { useUpdateAccountMutation } from 'api/accounts/updateAccount'
import { HHFormTextField, HHFormSwitchField, HHFormPriceField, HHFormDesktopDatePicker } from 'components/form-fields/v5'
import { addBillingPOAndTax } from 'utils/form-body'
import { getExistingCustomerInfo } from 'utils/customerPayload'
import {
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_PAYMENT_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNT_DETAILS_BY_ID,
  CACHE_TAG_ACCOUNT_GROUP_DETAILS,
  CACHE_TAG_AGING_GROUP_DETAILS,
  CACHE_TAG_SUSPENSION_GROUP_DETAILS,
} from 'api/cacheTagTypes'
import { getAccountMeta } from 'data/meta/accountMetaSelectors'
import { isCustomerDetailsPage } from 'router/routes'

import T from 'T'
import api from 'api'
import Tag from 'components/tags/Tag'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import DragPinMessage from 'components/common/address/DragPinMessage'
import BusinessUnitDropdown from 'components/customer-details/content/common/business-unit-dropdown/BusinessUnitDropdown'
import { hasBillingEmail } from 'components/customer-details/content/common/no-billing-email-dialog/hasBillingEmail'
import NoBillingEmailDialog from 'components/customer-details/content/common/no-billing-email-dialog/NoBillingEmailDialog'
import { formatDateToBEFormatDateFns } from 'utils/date'
import LocationContent from '../../location/LocationContent'
import MapView from '../../location/MapView'
import AccountForm from './AccountForm'
import AccountContent from '../AccountContent'
import { EDIT_ACCOUNT_INITIAL_STATE } from './model'

const EditAccountDialog = ({ isOpen = false, customerDetails = {}, account, onClose }) => {
  const customerMeta = useSelector(getAccountMeta, shallowEqual)
  const dispatch = useDispatch()
  const [updateAccount, { isLoading: isUpdateAccountLoading }] = useUpdateAccountMutation()
  const metaTags = get(customerMeta, 'tags', [])

  const initialState = account || EDIT_ACCOUNT_INITIAL_STATE
  const { latitude: initialLatitude, longitude: initialLongitude } = initialState.billingAddress
  const [latLngForMap, setLatLngForMap] = useState({
    latitudeForMap: initialLatitude,
    longitudeForMap: initialLongitude,
  })
  const [latLngForMapHistory, setLatLngForMapHistory] = useState({
    latitudeForMapHistory: initialLatitude,
    longitudeForMapHistory: initialLongitude,
  })
  const { latitudeForMap, longitudeForMap } = latLngForMap
  const { latitudeForMapHistory, longitudeForMapHistory } = latLngForMapHistory

  const editAccountForm = useForm({ defaultValues: { ...EDIT_ACCOUNT_INITIAL_STATE } })
  const { handleSubmit, control, watch, setValue, resetField, reset, setFocus } = editAccountForm
  const [isMissingBillingEmail, setIsMissingBillingEmail] = useState(false)
  const watchAddress = watch('billingAddress')

  const onSubmitHandler = data => {
    const accountBilling = get(data, 'accountBilling', {})
    const contractExpirationEnable = get(data, 'contractExpirationEnable', false)
    const contractExpirationDate = get(data, 'contractExpirationDate')
    const payload = {
      ...data,
      accountName: trim(get(data, 'accountName', '')),
      // append new tags
      ...getExistingCustomerInfo({ ...customerDetails, tags: get(data, 'tags', []) }),
      ...(contractExpirationEnable && {
        contractExpirationDate: formatDateToBEFormatDateFns(contractExpirationDate),
      }),
      accountBilling: {
        ...accountBilling,
        ...addBillingPOAndTax(accountBilling),
      },
    }

    const accountId = data.id

    updateAccount(payload)
      .unwrap()
      .then(() => {
        if (isCustomerDetailsPage()) {
          dispatch(getCustomerDetails({ accountId }))
        }
        dispatch(
          api.util.invalidateTags([
            CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
            // cross check if we need to invalidate the CACHE_TAG_ACCOUNT_INVOICE_LIST & CACHE_TAG_ACCOUNT_PAYMENT_LIST
            { type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId },
            { type: CACHE_TAG_ACCOUNT_PAYMENT_LIST, id: accountId },
            CACHE_TAG_ACCOUNT_LIST,
            { type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId },
            CACHE_TAG_ACCOUNT_GROUP_DETAILS,
            CACHE_TAG_AGING_GROUP_DETAILS,
            CACHE_TAG_SUSPENSION_GROUP_DETAILS,
          ])
        )
        toast.success(T.ACCOUNT_DETAILS_UPDATED_SUCCESSFULLY)
        onClose()
      })
      .catch(handleError)
  }

  const handleLatLongFromMapView = (long, lat) => {
    setValue('billingAddress.longitude', long)
    setValue('billingAddress.latitude', lat)
    setLatLngForMap({ longitudeForMap: long, latitudeForMap: lat })
  }

  // The account keeps printing until it has an email to send to, so turning it off snaps back on.
  const handleReceivesPrintedCopyChange = React.useCallback(
    receivesPrintedCopy => {
      if (receivesPrintedCopy || hasBillingEmail(get(customerDetails, 'contacts', []))) return
      setValue('accountBilling.receivesPrintedCopy', true)
      setIsMissingBillingEmail(true)
    },
    [customerDetails, setValue]
  )

  const handleCloseMissingBillingEmail = React.useCallback(() => setIsMissingBillingEmail(false), [])

  const handleBusinessUnitChange = React.useCallback(
    businessUnitId => setValue('businessUnitId', businessUnitId, { shouldDirty: true }),
    [setValue]
  )

  useEffect(() => {
    if (isOpen) {
      if (account) {
        reset(account, { keepDefaultValues: true })
      }
      setLatLngForMapHistory({ longitudeForMapHistory: initialLongitude, latitudeForMapHistory: initialLatitude })
      handleLatLongFromMapView(initialLongitude, initialLatitude)
    }
  }, [isOpen, account])

  return (
    <>
      <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth disableScrollLock>
        <HHDialogTitle title={T.EDIT_ACCOUNT_DETAILS} onClose={onClose} />
        <DialogContent>
          <Grid container mt={2} columnGap={3}>
            <Grid
              container
              spacing={2}
              size={{
                xs: 12,
                md: 'grow',
              }}
            >
              <Grid
                minHeight={335}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <MapView
                  longitude={longitudeForMap}
                  latitude={latitudeForMap}
                  longitudeForMapHistory={longitudeForMapHistory}
                  latitudeForMapHistory={latitudeForMapHistory}
                  onChange={({ mapLng, mapLat }) => handleLatLongFromMapView(mapLng, mapLat)}
                  onReset={() => handleLatLongFromMapView(longitudeForMapHistory, latitudeForMapHistory)}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <AccountContent
                  accountNumber={get(customerDetails, 'accountNumber')}
                  accountName={watch('accountName')}
                  attention={watch('billingAddress.attention')}
                  showPlaceholder
                />
                <Box mt={1.5}>
                  <LocationContent address={watchAddress} showPlaceholder />
                </Box>
                <Grid mt={1.5} container spacing={0.5} alignItems="center">
                  {Children.toArray(
                    watch('tags').map(tag => (
                      <Grid>
                        <Tag clickable={false} tag={tag} />
                      </Grid>
                    ))
                  )}
                </Grid>
              </Grid>
              <DragPinMessage latitude={latitudeForMap} longitude={longitudeForMap} />
              <BusinessUnitDropdown
                disabled={watch('businessUnitLocked')}
                value={watch('businessUnitId') || ''}
                onChange={handleBusinessUnitChange}
              />
              <Grid size={12}>
                <HHFormSwitchField
                  label={T.CUSTOMER_PURCHASE_ORDER}
                  control={control}
                  name="accountBilling.customerPurchaseOrder"
                  onChange={checked => {
                    if (!checked) {
                      resetField('accountBilling.po')
                      resetField('accountBilling.maxPoAmount')
                      return
                    }

                    setTimeout(() => setFocus('accountBilling.po'), 100)
                  }}
                />
              </Grid>
              {watch('accountBilling.customerPurchaseOrder') && (
                <>
                  <Grid size={6}>
                    <HHFormTextField
                      control={control}
                      name="accountBilling.po"
                      label={T.PO_NUMBER_PLACEHOLDER}
                      placeholder={T.PO_NUMBER_PLACEHOLDER}
                      fullWidth
                      deprecatedLabel={false}
                    />
                  </Grid>
                  <Grid size={6}>
                    <HHFormPriceField
                      dollarPrefix
                      control={control}
                      name="accountBilling.maxPoAmount"
                      label={T.MAX_PO_AMOUNT}
                      placeholder={T.MAX_PO_AMOUNT}
                      fullWidth
                      deprecatedLabel={false}
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <HHFormSwitchField
                  label={capitalize(T.TAX_EXEMPT)}
                  control={control}
                  name="accountBilling.taxExemptEnable"
                  onChange={checked => {
                    if (!checked) {
                      resetField('accountBilling.taxExempt')
                      return
                    }

                    setTimeout(() => setFocus('accountBilling.taxExempt'), 100)
                  }}
                />
              </Grid>
              {watch('accountBilling.taxExemptEnable') && (
                <Grid size={12}>
                  <HHFormTextField
                    control={control}
                    name="accountBilling.taxExempt"
                    label={T.TAX_IDENTIFIER}
                    placeholder={T.TAX_IDENTIFIER}
                    fullWidth
                    deprecatedLabel={false}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Box alignItems="center" display="flex" gap={0.5}>
                  <HHFormSwitchField
                    control={control}
                    label={T.RECEIVES_PRINTED_COPY}
                    name="accountBilling.receivesPrintedCopy"
                    onChange={handleReceivesPrintedCopyChange}
                  />
                  <Tooltip title={T.RECEIVES_PRINTED_COPY_TOOLTIP}>
                    <InfoOutlinedIcon color="disabled" fontSize="small" />
                  </Tooltip>
                </Box>
              </Grid>

              {/* Contract expiration toggle + date picker */}
              <Grid size={12}>
                <HHFormSwitchField
                  label="Contract expiration"
                  control={control}
                  name="contractExpirationEnable"
                  onChange={checked => {
                    if (!checked) {
                      resetField('contractExpirationDate')
                      return
                    }

                    setTimeout(() => setFocus('contractExpirationDate'), 100)
                  }}
                />
              </Grid>
              {watch('contractExpirationEnable') && (
                <Grid size={12}>
                  <HHFormDesktopDatePicker
                    control={control}
                    name="contractExpirationDate"
                    label="Contract expiration date"
                    placeholder={T.SELECT_EXPIRATION_DATE}
                    deprecatedLabel={false}
                    TextFieldProps={{
                      fullWidth: false,
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <FormProvider {...editAccountForm}>
              <AccountForm metaTags={metaTags} setLatLngForMap={setLatLngForMap} setLatLngForMapHistory={setLatLngForMapHistory} />
            </FormProvider>
          </Grid>
        </DialogContent>
        <HHDialogActions>
          <CancelButton onClick={onClose} />
          <SaveButton loading={isUpdateAccountLoading} onClick={handleSubmit(onSubmitHandler)} />
        </HHDialogActions>
      </HHBaseDialog>
      <NoBillingEmailDialog isOpen={isMissingBillingEmail} onClose={handleCloseMissingBillingEmail} />
    </>
  )
}

EditAccountDialog.propTypes = {
  isOpen: PropTypes.bool,
  customerDetails: PropTypes.object,
  account: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

export default EditAccountDialog
